import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import { Nullable } from '@client/core/data/types';
import { parseISO } from '@client/core/utils/date';
import { Company } from '@client/features/company/models/company';
import { SoftwareFeature } from '@client/features/software-feature/types';
import { Subscription } from '@client/features/subscription/models/subscription';
import { toEnum } from '@client/core/data/concerns/parses-from-json/to-enum';

/**
 * Server state available to the client application for the currently authenticated User.
 *
 * @see app/Http/Resources/Auth/AuthUserResource.php
 */
export class AuthUser {
  constructor(
    public readonly id: number,
    public readonly uuid: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly avatar: Nullable<string>,
    public readonly email: string,
    public readonly phone: string,
    public readonly title: string,
    public readonly isAdmin: boolean,
    public readonly isOwner: boolean,
    public readonly isTeamAdmin: boolean,
    public readonly isTeamMember: boolean,
    public readonly isImpersonated: boolean,
    public readonly company: Nullable<Company>,
    public readonly subscription: Nullable<Subscription>,
    /** @deprecated Instead use featuresAllowed */
    public readonly featureAccess: { [key in SoftwareFeature]?: boolean },
    public readonly featuresAllowed: Array<SoftwareFeature>,
    public readonly amountPaid: number,
    public readonly siteKey: string,
    public readonly firstPaymentAt: Nullable<Date>,
    public readonly lastPaymentAt: Nullable<Date>,
    public readonly totalNumLogins: number,
    public readonly lastLoginAt: Nullable<Date>,
    public readonly workspaceOwnerId: Nullable<number>,
  ) {}

  /**
   * Whether this User can Invite Users and manage permissions for Workspace Users.
   */
  canManageUsers(): boolean {
    // TODO: Make this factor in Subscription Plan features
    return this.isOwner || this.isTeamAdmin;
  }
}

defineTransform(AuthUser, (json, { fromSnakeCaseKeys, nullOr }) => {
  return {
    ...fromSnakeCaseKeys(),
    firstPaymentAt: nullOr(json.first_payment_at, () => parseISO(json.first_payment_at)),
    lastPaymentAt: nullOr(json.last_payment_at, () => parseISO(json.last_payment_at)),
    lastLoginAt: nullOr(json.last_login_at, () => parseISO(json.last_login_at)),
    company: nullOr(json.company, () => fromJson(Company, json.company)),
    subscription: nullOr(json.subscription, () => fromJson(Subscription, json.subscription)),
    featuresAllowed: json.features_allowed.map((feature) => toEnum(SoftwareFeature, feature)),
  };
});
